import { FC, ReactElement, ReactNode, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import classNames from 'classnames'

import { Badge, LanguageIcon, Logo } from '@/components'
import { CollapsibleNavigation, IconButton, NavItem } from '@/compositions'
import { NAVIGATION_NOTIFICATIONS_SLOT_ID } from '@/helpers/notificationsSlot'
import { hasStreamAccess } from '@/helpers/streamAccess'
import { shouldShowSchedule } from '@/helpers/tools'
import { getLanguage, trans } from '@/helpers/trans'
import { logout } from '@/redux/app/appLegacyActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { removePushDeviceOnLogout } from '@/services/PushRegistration/pushRegistrationRef'

import './ConnectedNavigation.scss'

const lang = getLanguage()

const renderNavLabel = (label: string, hasUnread: boolean): ReactNode => (
    <>
        {label}
        {hasUnread && <Badge className="navigation__nav-item-dot" />}
    </>
)

// A "root page" is any page reachable directly from the navigation (a top-level item or one of its children). There's
// no meaningful "back" from those, so the list comes from the nav items rather than listing the paths again.
const isRootPage = (navItems: Record<string, NavItem>, pathname: string): boolean => {
    const rootPagePaths = Object.values(navItems)
        .flatMap(({ link, children }) => (children.length
            ? children.map(child => `${link}${child.link}`)
            : [link.replace(/\/$/, '')]))

    return rootPagePaths.includes(pathname)
}

// Detail and action pages (a shift, an absence, a channel, ...) aren't in the navigation, but they're always one path
// segment below a page that does exist (a real page, or a route that redirects to one). Dropping the last segment lands
// on that parent page instead of wherever the browser history points.
const getParentPath = (pathname: string): string | null => {
    const shiftExchangeDetailPath = `/${trans('nav.exchanges.shiftExchange.link')}`

    // This route lives outside the exchanges section, so dropping its last segment doesn't land on a page
    if (pathname.startsWith(`${shiftExchangeDetailPath}/`)) {
        return `/${trans('nav.exchanges.link')}/${trans('nav.exchanges.myExchanges.link')}`
    }

    const segments = pathname.split('/').filter(Boolean)
    segments.pop()

    return segments.length ? `/${segments.join('/')}` : null
}

export const ConnectedNavigation: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { settings, currentUser } = useTypedSelector(state => state.userReducer)
    const { feedUnreadCount, chatUnreadCount } = useTypedSelector(state => state.notificationsReducer)
    const showCommunication = hasStreamAccess(currentUser)
    const [collapsed, setCollapsed] = useState(true)
    // If the app gets the language from the url it's embedded in the ValkWorks app, which shows the language itself
    const [hasLanguageParams] = useState(() => new URLSearchParams(location.search).has('language'))

    const toggleNavBar = (): void => {
        setCollapsed(! collapsed)
    }

    const handleLogoutClick = (): void => {
        removePushDeviceOnLogout()
        dispatch(logout())
    }

    const handleBackButtonClick = (): void => {
        const parentPath = getParentPath(location.pathname)

        if (parentPath) {
            navigate(parentPath)
        } else {
            navigate(-1)
        }

        setCollapsed(true)
    }

    const showSchedule = shouldShowSchedule(settings, currentUser?.departments)
    const hasCommunicationUnread = feedUnreadCount > 0 || chatUnreadCount > 0
    const navItems: Record<string, NavItem> = {
        [trans('nav.tracks.link')]: {
            label: trans('nav.tracks.label'),
            link: `/${trans('nav.tracks.link')}/`,
            icon: 'target',
            children: [],
        },
        [trans('nav.shifts.link')]: {
            label: trans('nav.shifts.label'),
            link: `/${trans('nav.shifts.link')}/`,
            icon: 'calendar',
            children: [
                {
                    label: trans('nav.shifts.myShifts.label'),
                    link: trans('nav.shifts.myShifts.link'),
                },
                ...(showSchedule ? [{
                    label: trans('nav.shifts.schedule.label'),
                    link: trans('nav.shifts.schedule.link'),
                }] : []),
                {
                    label: trans('nav.shifts.openShifts.label'),
                    link: trans('nav.shifts.openShifts.link'),
                },
            ],
        },
        [trans('nav.exchanges.link')]: {
            label: trans('nav.exchanges.label'),
            link: `/${trans('nav.exchanges.link')}/`,
            icon: 'repeat',
            children: [
                {
                    label: trans('nav.exchanges.myExchanges.label'),
                    link: trans('nav.exchanges.myExchanges.link'),
                },
                {
                    label: trans('nav.exchanges.offeredExchanges.label'),
                    link: trans('nav.exchanges.offeredExchanges.link'),
                },
            ],
        },
        [trans('nav.absences.link')]: {
            label: trans('nav.absences.label'),
            link: `/${trans('nav.absences.link')}/`,
            icon: 'heart',
            children: [
                {
                    label: trans('nav.absences.leaveOfAbsenceOverview.label'),
                    link: trans('nav.absences.leaveOfAbsenceOverview.link'),
                },
                {
                    label: trans('nav.absences.loketHoursPerPeriod.label'),
                    link: trans('nav.absences.loketHoursPerPeriod.link'),
                },
                {
                    label: trans('nav.absences.leaveOfAbsenceRequests.label'),
                    link: trans('nav.absences.leaveOfAbsenceRequests.link'),
                },
                {
                    label: trans('nav.absences.absences.label'),
                    link: trans('nav.absences.absences.link'),
                },
                {
                    label: trans('nav.absences.createAbsences.label'),
                    link: trans('nav.absences.createAbsences.link'),
                },
            ],
        },
        [trans('nav.documents.link')]: {
            label: trans('nav.documents.label'),
            link: `/${trans('nav.documents.link')}/`,
            icon: 'file-text',
            children: [],
        },
        ...(showCommunication && {
            [trans('nav.communication.link')]: {
                label: renderNavLabel(trans('nav.communication.label'), hasCommunicationUnread),
                link: `/${trans('nav.communication.link')}/`,
                icon: 'chat-bubble',
                children: [
                    {
                        label: renderNavLabel(trans('nav.communication.newsfeed.label'), feedUnreadCount > 0),
                        link: trans('nav.communication.newsfeed.link'),
                    },
                    {
                        label: renderNavLabel(trans('nav.communication.messages.label'), chatUnreadCount > 0),
                        link: trans('nav.communication.messages.link'),
                    },
                ],
            },
        }),
        [trans('nav.planningPreferences.link')]: {
            label: trans('nav.planningPreferences.label'),
            link: `/${trans('nav.planningPreferences.link')}/`,
            icon: 'star',
            children: [],
        },
        [trans('nav.profile.link')]: {
            label: trans('nav.profile.label'),
            link: `/${trans('nav.profile.link')}/`,
            icon: 'user',
            children: [],
        },
        [trans('nav.language.link')]: {
            label: (
                <>
                    <LanguageIcon locale={lang.code} />
                    {trans(`common.language.${lang.code}`)}
                </>
            ),
            link: `/${trans('nav.language.link')}/`,
            children: [],
        },
    }

    // Translations are loaded after the modules are, so these can't be computed at module level
    const headers = [
        trans('nav.tracks.link'),
        ...(showCommunication ? [trans('nav.communication.link')] : []),
        trans('nav.shifts.link'),
        trans('nav.exchanges.link'),
        trans('nav.absences.link'),
        trans('nav.documents.link'),
        trans('nav.planningPreferences.link'),
        trans('nav.profile.link'),
        ...(! hasLanguageParams ? [trans('nav.language.link')] : []),
    ]

    const isOnRootPage = isRootPage(navItems, location.pathname)

    return (
        <div className="navigation">
            <div className="navigation__top">
                <IconButton
                    hideLabel
                    disabled={isOnRootPage}
                    icon="arrow"
                    text={trans('common.back')}
                    onClick={handleBackButtonClick}
                    className={classNames('navigation__back-button', {
                        'navigation__back-button--is-hidden': isOnRootPage,
                    })}
                    iconClassName="navigation__back-button-icon"
                />
                <NavLink to="/" className="navigation__brand-link">
                    <Logo isBlue className="navigation__brand" />
                </NavLink>
                <div className="navigation__top-actions">
                    <div id={NAVIGATION_NOTIFICATIONS_SLOT_ID} className="navigation__notifications-slot" />
                    <span className="navigation__toggle-button-wrap">
                        <IconButton
                            hideLabel
                            icon="hamburger"
                            text="hamburger"
                            onClick={toggleNavBar}
                            className="navigation__toggle-button"
                            iconClassName="navigation__toggle-button-icon"
                        />
                        {hasCommunicationUnread && <Badge className="navigation__toggle-button-dot" />}
                    </span>
                </div>
            </div>
            <CollapsibleNavigation
                isActive={! collapsed}
                navItems={navItems}
                navItemsHeaders={headers}
                onLogoutClick={handleLogoutClick}
                onNavLinkClick={toggleNavBar}
            />
        </div>
    )
}
